import { FunctionComponent, useContext, useState } from 'react';
import styled from 'styled-components';
import { PrimaryButton } from '@stax-core/truffle-components';
import {
  PageHeader,
  Button,
  ButtonProps,
  Group,
  ResponsivePadding,
} from '../../components';
import { BrandStore, RoutesStore } from '../../context';
import { history } from '../../history';
import { useNextApplicationPath } from '../../hooks';
import {
  TermsAndConditionsModal,
  useTocHelper,
} from './TermsAndConitionsModal';
import { RouteItem } from '../../routes/RouteItem';
import { parse } from '../../util/api.util';

const LinkButton: FunctionComponent<ButtonProps> = props => (
  <Button variant="link" inline style={{ margin: 0 }} {...props} />
);

const StyledSection = styled.div`
  padding: 0 2%;
  display: flex;
  flex-direction: row;

  > div {
    flex-direction: row;
    width: 50%;

    > img {
      height: 350px;
    }
  }

  margin-left: calc(-80px - 2%);
  z-index: 3;

  @media (max-width: 1000px) {
    margin-left: calc(-50px - 2%);

    > div:first-child {
      width: 100%;
    }
  }
`;

const InlineItem = styled.div`
  line-height: 0.4;

  > span {
    text-align: center;
    display: inline-block;
    color: ${(props: any) => props.theme.colors.accentText};
    font-size: 1.3rem;
    width: 80px;

    @media (max-width: 1000px) {
      width: 50px;
    }
  }

  > h3 {
    padding-left: 1.5rem;
    display: inline-block;

    @media (max-width: 1000px) {
      padding-left: 0.5rem;
    }
  }
`;

const FooterSection = styled(Group)`
  padding: 24px;
  display: flex;
  flex-direction: column;
  & > button {
    width: 8rem;
  }
`;

export const WelcomePage: FunctionComponent = () => {
  const { state: { brand } } = useContext(BrandStore);
  const [showToc, setShowToc] = useState(false);
  const { registerAcceptance } = useTocHelper();
  const nextPath = useNextApplicationPath();
  const { sidebarRoutes } = useContext(RoutesStore);
  const [isRegisteringAcceptance, setIsRegisteringAcceptance] = useState(false);

  const { regWelcomeHeader, regWelcomeBody } = brand || {};
  const queryParams = parse(window.location.search);
  if (queryParams?.oauth_state_id) {
    history.replace('/callback');
  }

  return (
    <>
      <PageHeader
        id="welcome-page-header"
        data-testid="welcome-page-header"
        header={regWelcomeHeader}
        description={regWelcomeBody}
      ></PageHeader>

      <StyledSection>
        <div>
          {(sidebarRoutes as RouteItem[])
            // slice the review item off
            .slice(0, -1)
            .map((routeItem, i) => (
              <InlineItem key={i}>
                <span>
                  <i
                    className={routeItem.icon as string}
                    data-testid={`welcome-${routeItem.path}`}
                  />
                </span>
                <h3>{routeItem.title}</h3>
              </InlineItem>
            ))}
        </div>
      </StyledSection>
      <ResponsivePadding>
        <FooterSection justify="end">
          <p>
            Before you can accept payments, we need to learn more about you and
            your business. By clicking continue below, you agree to provide us
            with information to comply with requirements from regulators,
            financial partners and the terms of our{' '}
            <LinkButton
              data-testid="show-toc-button"
              onClick={_ => setShowToc(true)}
            >
              Services Agreement
            </LinkButton>
            .
          </p>

          <PrimaryButton
            style={{ width: '100%' }}
            id="welcome-continue-button"
            data-testid="welcome-continue-button"
            trackingId="welcome-continue-button"
            onClick={async () => {
              setIsRegisteringAcceptance(true);
              await registerAcceptance();
              setIsRegisteringAcceptance(false);
              history.push(nextPath!);
            }}
          >
            <span> {isRegisteringAcceptance ? 'Saving...' : 'Continue'}</span>
          </PrimaryButton>
        </FooterSection>
      </ResponsivePadding>
      <TermsAndConditionsModal showToc={showToc} setShowToc={setShowToc} />
    </>
  );
};

WelcomePage.defaultProps = {
  responsive: true,
};
